// Form → request shaping and validation for workflow generation.
//
// Kept separate from the dialog so the rules (which modes need a description, which need a
// template, which fields are omitted) are testable without a DOM.

export const MODES = [
  {
    value: "auto",
    label: "Auto",
    help: "Picks for you: template when a template/graph is supplied, otherwise LLM planning from your description.",
  },
  {
    value: "llm",
    label: "LLM planning",
    help: "The model designs the graph from the description, optionally using the node catalog. Needs a meaningful description.",
  },
  {
    value: "template",
    label: "Template",
    help: "Deterministic: builds from the chosen template (or validates a supplied graph). No LLM graph design.",
  },
];

export const KINDS = [
  {
    value: "agent",
    label: "Agent",
    help: "Agent/inference workflow: chat, RAG, dataset lookup, routing, Python processing.",
  },
  {
    value: "ingestion",
    label: "Ingestion",
    help: "Dataset ingestion workflow. The graph must end in a Chroma input node.",
  },
  {
    value: "graph",
    label: "Generic graph",
    help: "Custom graph with no agent/ingestion validation. Needs a graph or a template.",
  },
];

const trim = (value) => (typeof value === "string" ? value.trim() : "");

const toInt = (value, fallback = null) => {
  if (value === "" || value === undefined || value === null) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

/**
 * Build the POST body for /admin/workflow/generate.
 *
 * Optional fields are omitted rather than sent empty, so the endpoint's own defaults apply.
 */
const normalizeQuestions = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();

  return list
    .map((entry) => {
      if (typeof entry === "string") return entry.trim();
      if (entry && typeof entry === "object") {
        return String(entry.question ?? entry.text ?? entry.prompt ?? entry.label ?? entry.message ?? "").trim();
      }
      return "";
    })
    .filter((question) => question && !seen.has(question) && seen.add(question));
};

/**
 * Read a "needs more information" response from the generator.
 *
 * The API answers a normal 200 with `{ success: false, requires_clarification: true,
 * error_type: "clarification", message, questions: [...] }`. Returns the questions to put to the
 * user, or null when the response is a real result/failure.
 *
 * @param {object} results Response from POST /admin/workflow/generate.
 * @returns {{message: string|null, questions: string[]}|null}
 */
export const readClarification = (results) => {
  if (!results || typeof results !== "object") return null;

  const flagged =
    results.requires_clarification === true ||
    results.requiresClarification === true ||
    String(results.error_type ?? results.errorType ?? "").toLowerCase() === "clarification";

  const questions = normalizeQuestions(
    results.questions ?? results.follow_up_questions ?? results.missing_information ?? results.required_information
  );

  const message = typeof results.message === "string" && results.message.trim() ? results.message.trim() : null;

  // Flagged with no explicit questions: treat the message as the question so it can be answered.
  if (questions.length === 0) {
    if (!flagged) return null;
    return message ? { message, questions: [message] } : null;
  }

  return { message, questions };
};

/**
 * Pair the questions with the user's answers, dropping blanks.
 *
 * @param {string[]} questions
 * @param {Record<string, string>} answers
 * @returns {Record<string, string>}
 */
export const buildClarificationAnswers = (questions = [], answers = {}) => {
  const result = {};

  (Array.isArray(questions) ? questions : []).forEach((question) => {
    const value = typeof answers?.[question] === "string" ? answers[question].trim() : "";
    if (value) result[question] = value;
  });

  return result;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The client id to send with a generate request.
 *
 * The generator resolves `client_id` as a client **public id** (a UUID). Numeric database ids
 * (the user's `activeUser.client_id`, the record's `client_id` foreign key) produce
 * "Client not found: <n>" server-side, so only ever send a UUID — the selected client's public
 * id, or the record's client id when it is already a UUID. Otherwise send nothing and let the
 * server infer the client from the authenticated user.
 *
 * @param {object|null} selectedClient The app's selected client.
 * @param {object|null} [record] The agent / workflow / dataset being edited.
 * @returns {string|null}
 */
export const resolveGeneratorClientId = (selectedClient, record = null) => {
  const candidates = [selectedClient?.public_id, record?.client_id, selectedClient?.client_id];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && UUID_PATTERN.test(candidate)) return candidate;
  }

  return null;
};

export const buildGenerateRequest = (form = {}) => {
  const mode = form.mode || "auto";
  const kind = form.kind || "agent";

  const request = {
    kind,
    mode,
    save: form.save !== false,
    verify: Boolean(form.verify),
    allow_python: Boolean(form.allowPython),
  };

  const name = trim(form.name);
  if (name) request.name = name;

  const description = trim(form.description);
  if (description) request.description = description;

  const purpose = trim(form.purpose);
  if (purpose) request.purpose = purpose;

  const template = trim(form.template);
  if (template && template !== "none") request.template = template;

  if (form.clientId) request.client_id = form.clientId;

  // Answers to a previous clarification round; the generation is stateless, so the original
  // description/options are sent again alongside them.
  if (form.clarificationAnswers && Object.keys(form.clarificationAnswers).length > 0) {
    request.clarification_answers = form.clarificationAnswers;
  }
  if (form.options && Object.keys(form.options).length > 0) request.options = form.options;

  // Model selection. An inline model_config takes priority over a registered name server-side.
  const model = trim(form.model);
  if (model) request.model = model;

  if (form.modelConfig && Object.keys(form.modelConfig).length > 0) {
    request.model_config = form.modelConfig;
  }

  if (form.useSecondaryEndpoint) request.use_secondary_endpoint = true;

  if (request.verify) {
    request.verify_cycles = toInt(form.verifyCycles, 2);
  }

  const maxRepairs = toInt(form.maxRepairs);
  if (maxRepairs !== null) request.max_repairs = maxRepairs;

  const maxExpansions = toInt(form.maxExpansions);
  if (maxExpansions !== null) request.max_expansions = maxExpansions;

  // Output budget for the generation model (the model selector's "Max output tokens").
  const maxTokens = toInt(form.maxTokens);
  if (maxTokens !== null) request.max_tokens = maxTokens;

  return request;
};

/**
 * Check the form before calling the endpoint.
 *
 * @returns {{ok: boolean, errors: string[]}}
 */
export const validateGenerateForm = (form = {}) => {
  const errors = [];
  const mode = form.mode || "auto";
  const template = trim(form.template);
  const description = trim(form.description);
  const purpose = trim(form.purpose);

  // No name requirement: the name comes from the agent this is attached to, or the generator
  // supplies one.

  if (mode === "llm" && !description && !purpose) {
    errors.push("LLM planning needs a description or purpose to work from.");
  }

  if (mode === "template" && (!template || template === "none")) {
    errors.push("Template mode needs a template to build from.");
  }

  if (mode === "auto" && !description && !purpose && (!template || template === "none")) {
    errors.push("Describe the workflow, or choose a template.");
  }

  if (form.verify) {
    const cycles = toInt(form.verifyCycles, 2);
    if (cycles === null || cycles < 0) {
      errors.push("Verification cycles must be zero or more.");
    }
  }

  const maxRepairs = toInt(form.maxRepairs);
  if (maxRepairs !== null && maxRepairs < 0) {
    errors.push("Max repairs cannot be negative.");
  }

  const maxExpansions = toInt(form.maxExpansions);
  if (maxExpansions !== null && maxExpansions < 0) {
    errors.push("Max expansions cannot be negative.");
  }

  const maxTokens = toInt(form.maxTokens);
  if (maxTokens !== null && maxTokens <= 0) {
    errors.push("Max output tokens must be greater than zero.");
  }

  return { ok: errors.length === 0, errors };
};

/**
 * Normalise the templates endpoint into picker options. The list's key field isn't fixed, so
 * the first recognisable identifier wins.
 */
export const normalizeTemplates = (templates) => {
  const list = Array.isArray(templates) ? templates : [];

  return list
    .map((template) => {
      if (typeof template === "string") {
        return { value: template, label: template };
      }

      const value =
        template?.name ?? template?.key ?? template?.id ?? template?.slug ?? template?.template;
      if (!value) return null;

      return {
        value: String(value),
        label: template?.label ?? template?.title ?? String(value),
        kind: template?.kind ?? null,
      };
    })
    .filter(Boolean);
};
